import { Dispatch, SetStateAction, useCallback, useContext, useRef } from 'react';
import { TSelectedItem, TSelectItemDialogState, TSelectVersionDialogState, useGetVersionInfo } from '@modules/items';
import {
  EHeadingsSelectionMode,
  TDraft,
  TRefVersionMode,
  TSelectCaptionDialogState,
  TSelectHeadingDialogState,
  TVersion,
} from '../types';
import { OutRefBookmarkKind } from '../tiptap/nodes/caption/constants';
import { TFileVersion } from '@modules/files';
import isBlockDocument from '../helpers/isBlockDocument.helper';
import { ToastContext } from '@shared/contexts';
import { TOptionsDialogState } from '@shared/types';
import { captionLabelMap } from '@shared/i18n';
import { SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE } from '@modules/documents/constants';
import { TERMINOLOGY } from '@shared/i18n';
import DocumentContext from '../contexts/DocumentContext.context';

type TParams = {
  setSelectItemDialogState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
  setSelectVersionDialogState: Dispatch<SetStateAction<TSelectVersionDialogState | undefined>>;
  setSelectHeadingDialogState: Dispatch<SetStateAction<TSelectHeadingDialogState | undefined>>;
  setSelectCaptionDialogState: Dispatch<SetStateAction<TSelectCaptionDialogState | undefined>>;
  setOptionsDialogState: Dispatch<SetStateAction<TOptionsDialogState | undefined>>;
};

const useInsertReference = ({
  setSelectItemDialogState,
  setSelectVersionDialogState,
  setSelectHeadingDialogState,
  setSelectCaptionDialogState,
  setOptionsDialogState,
}: TParams) => {
  const { doc, editor, captionsMap } = useContext(DocumentContext);
  // Read when an item is picked: a save can land while the picker is open.
  const refDoc = useRef(doc);
  refDoc.current = doc;
  const { show } = useContext(ToastContext);
  const getVersionInfo = useGetVersionInfo();

  const insertReference = (
    selectedItem: TSelectedItem,
    versionMode: TRefVersionMode,
    versionId?: string,
    headingId?: string
  ) => {
    if (isBlockDocument(selectedItem)) {
      return show('error', `${TERMINOLOGY.block.upper} cannot be inserted as a ${TERMINOLOGY.reference.lower}!`);
    }
    if (selectedItem.type === 'INLINE_BLOCK') {
      return show('error', `Cannot insert inline block!`);
    }
    if (selectedItem.type === 'FOLDER') return show('error', `Cannot reference folder!`);
    if (selectedItem.type === 'REPLICA_FILE' || selectedItem.type === 'REPLICA_DOCUMENT')
      return show(
        'error',
        `${TERMINOLOGY.reference.upperPlural} to ${TERMINOLOGY.replica.lowerPlural} are currently deactivated!`
      );
    editor
      .chain()
      .focus()
      .insertReference(selectedItem._id, selectedItem.type, versionMode, versionId, headingId)
      .run();
    setSelectHeadingDialogState((prev) => ({ ...prev, isOpen: false }) as TSelectHeadingDialogState);
  };

  const handleInsertReference = useCallback(() => {
    const openHeadingDialog = (
      version: TVersion | TDraft,
      selectedItem: TSelectedItem,
      versionMode: TRefVersionMode,
      versionId?: string
    ) => {
      const isSelfRef = versionMode === 'SELF';
      const isHeadingsPresent = version.headings.length > 0;

      if (isSelfRef && !isHeadingsPresent) return show('error', 'There are no sections in this document!');

      const mode = isSelfRef ? EHeadingsSelectionMode.SPECIFIC : EHeadingsSelectionMode.ALL;

      let lockedModeMessage;
      if (isSelfRef) lockedModeMessage = SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE.SELF_REF;
      else if (!isHeadingsPresent) lockedModeMessage = SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE.NO_HEADINGS;

      setSelectHeadingDialogState({
        isOpen: true,
        documentId: selectedItem._id,
        version,
        mode,
        modeLocked: {
          isModeLocked: isSelfRef || !isHeadingsPresent,
          lockedModeMessage,
        },
        onSelect: async (_, newSelectedIds) => {
          insertReference(selectedItem, versionMode, versionId, Array.from(newSelectedIds)[0]);
        },
      });
    };

    const openCaptionDialog = (selectedItem: TSelectedItem, kind: OutRefBookmarkKind) => {
      if (!captionsMap) return;

      setSelectCaptionDialogState({
        isOpen: true,
        captionKind: kind,
        captionsMap,
        onSelect: (captionId: string) => {
          editor
            .chain()
            .focus()
            .insertReference(selectedItem._id, 'DOCUMENT', 'SELF', undefined, undefined, {
              id: captionId,
              kind,
            })
            .run();
        },
      });
    };

    const getAvailableCaptionKinds = (): OutRefBookmarkKind[] => {
      if (!captionsMap) return [];

      const kinds = new Set<OutRefBookmarkKind>();
      for (const entry of Object.values(captionsMap)) {
        kinds.add(entry.kind);
      }
      return Object.values(OutRefBookmarkKind).filter((k) => kinds.has(k));
    };

    const handleSelfReference = (version: TVersion | TDraft, selectedItem: TSelectedItem) => {
      const hasHeadings = version.headings.length > 0;
      const availableCaptionKinds = getAvailableCaptionKinds();
      const hasCaptions = availableCaptionKinds.length > 0;

      if (!hasHeadings && !hasCaptions) {
        return show('error', 'There are no sections, tables, or figures in this document!');
      }

      const totalOptions = (hasHeadings ? 1 : 0) + availableCaptionKinds.length;

      if (totalOptions === 1) {
        if (hasHeadings) {
          openHeadingDialog(version, selectedItem, 'SELF');
        } else {
          openCaptionDialog(selectedItem, availableCaptionKinds[0]);
        }
        return;
      }

      const dialogState: TOptionsDialogState = {
        isOpen: true,
        title: 'What do you want to reference?',
        options: [],
      };

      if (hasHeadings) {
        dialogState.options.push({
          value: 'section',
          title: 'Section',
          tooltip: 'Reference a section in this document.',
          onClick: () => {
            setOptionsDialogState({ ...dialogState, isOpen: false });
            openHeadingDialog(version, selectedItem, 'SELF');
          },
        });
      }

      for (const kind of availableCaptionKinds) {
        const label = captionLabelMap.EN[kind];
        dialogState.options.push({
          value: kind,
          title: label,
          subtitle: 'by caption',
          tooltip: `Reference a ${label.toLowerCase()} by its caption label.`,
          onClick: () => {
            setOptionsDialogState({ ...dialogState, isOpen: false });
            openCaptionDialog(selectedItem, kind);
          },
        });
      }

      setOptionsDialogState(dialogState);
    };

    setSelectItemDialogState({
      isOpen: true,
      onSelect: async (selectedItems) => {
        const selectedItem = selectedItems[0];
        const { _id, type, name } = selectedItem;
        if (isBlockDocument(selectedItem)) {
          return show('error', `${TERMINOLOGY.block.upper} cannot be inserted as a ${TERMINOLOGY.reference.lower}!`);
        }
        // The open document already holds its draft, and the versions route refuses a document a feature owns.
        if (_id === refDoc.current._id) {
          setSelectItemDialogState(undefined);
          if (refDoc.current.draft) handleSelfReference(refDoc.current.draft, selectedItem);
          return;
        }
        const versionInfo = await getVersionInfo.mutateAsync({ id: _id, itemType: type });
        if (['REPLICA_FILE', 'REPLICA_DOCUMENT'].includes(type))
          return show(
            'error',
            `${TERMINOLOGY.reference.upperPlural} for ${TERMINOLOGY.replica.lowerPlural} are deactivated in your organization!`
          );
        if (!versionInfo.draft && !versionInfo.versions.length)
          return show('error', `You cannot reference an empty document. Please create a draft for ${name}!`);
        setSelectItemDialogState(undefined);

        setSelectVersionDialogState({
          title: 'Select version to reference',
          isOpen: true,
          versionInfo,
          onSelect: async (versionMode, versionId) => {
            let version: TVersion | TFileVersion | TDraft | undefined;
            if (versionMode === 'AUTO') version = versionInfo.draft ? versionInfo.draft : versionInfo.versions[0];
            else
              version =
                versionId === 'draft' ? versionInfo.draft : versionInfo.versions.find((v) => v.id === versionId);
            if (!version) {
              console.error(`Version not found for referencing! (${versionMode}, ${versionId})`);
              return show('error', 'Action failed!');
            }

            if (type === 'DOCUMENT' && (version as TVersion | TDraft).headings.length > 0)
              openHeadingDialog(version as TVersion | TDraft, selectedItem, versionMode, versionId);
            else insertReference(selectedItem, versionMode, versionId);
            setSelectVersionDialogState(undefined);
          },
        });
      },
    });
  }, [insertReference, getVersionInfo, captionsMap, setSelectCaptionDialogState, setOptionsDialogState]);

  return handleInsertReference;
};

export default useInsertReference;
